import {
  createDefaultDesktopState,
  DESKTOP_LAYOUT_KEY,
  DESKTOP_DEFAULTS,
  DESKTOP_PREFS_KEY,
  normalizeDesktopPrefs,
  normalizeDesktopState,
  saveDesktopPrefs,
  saveDesktopState,
} from "./desktopStorage"
import { normalizeProductivity, PRODUCTIVITY_DEFAULTS } from "./productivityStorage"

const DB_NAME = "holos-os"
const DB_VERSION = 1
const WORKSPACES = "workspaces"
const SNAPSHOTS = "snapshots"
const FALLBACK_PREFIX = "holos_workspace_v1:"
const DEVICE_KEY = "holos_device_id"
const MAX_SNAPSHOTS = 12
const MAX_FALLBACK_SNAPSHOTS = 3
let memoryDeviceId = ""

const requestResult = (request) => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result)
  request.onerror = () => reject(request.error)
})

const transactionDone = (transaction) => new Promise((resolve, reject) => {
  transaction.oncomplete = resolve
  transaction.onerror = () => reject(transaction.error)
  transaction.onabort = () => reject(transaction.error)
})

function openDatabase() {
  if (typeof indexedDB === "undefined") return Promise.resolve(null)
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(WORKSPACES)) db.createObjectStore(WORKSPACES, { keyPath: "accountId" })
      if (!db.objectStoreNames.contains(SNAPSHOTS)) {
        const snapshots = db.createObjectStore(SNAPSHOTS, { keyPath: "id" })
        snapshots.createIndex("accountId", "accountId", { unique: false })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function fallbackKey(accountId) {
  return `${FALLBACK_PREFIX}${accountId}`
}

function snapshotFallbackKey(accountId) {
  return `${FALLBACK_PREFIX}snapshots:${accountId}`
}

async function migrateLegacyWorkspace(accountId) {
  try {
    const legacyState = localStorage.getItem(DESKTOP_LAYOUT_KEY)
    const legacyPrefs = localStorage.getItem(DESKTOP_PREFS_KEY)
    if (!legacyState && !legacyPrefs) return null
    const workspace = {
      ...createWorkspace(
        accountId,
        legacyState ? JSON.parse(legacyState) : createDefaultDesktopState(),
        legacyPrefs ? JSON.parse(legacyPrefs) : DESKTOP_DEFAULTS,
      ),
      revision: 1,
      pending: true,
      updatedAt: new Date().toISOString(),
    }
    await saveLocalWorkspace(workspace)
    if (localStorage.getItem(fallbackKey(accountId))) {
      localStorage.removeItem(DESKTOP_LAYOUT_KEY)
      localStorage.removeItem(DESKTOP_PREFS_KEY)
    }
    return workspace
  } catch {
    return null
  }
}

function saveFallbackSnapshot(snapshot) {
  try {
    const existing = JSON.parse(localStorage.getItem(snapshotFallbackKey(snapshot.accountId)) ?? "[]")
    const next = [snapshot, ...(Array.isArray(existing) ? existing : [])].slice(0, MAX_FALLBACK_SNAPSHOTS)
    localStorage.setItem(snapshotFallbackKey(snapshot.accountId), JSON.stringify(next))
  } catch { /* snapshots remain best-effort when all browser storage is unavailable */ }
}

function loadFallbackSnapshots(accountId) {
  try {
    const value = JSON.parse(localStorage.getItem(snapshotFallbackKey(accountId)) ?? "[]")
    return Array.isArray(value) ? value.slice(0, MAX_FALLBACK_SNAPSHOTS) : []
  } catch {
    return []
  }
}

export function desktopAccountId(user) {
  if (!user) return ""
  return `user-${user.id ?? user.username}`
}

export function desktopDeviceId() {
  let id = memoryDeviceId
  try { id ||= localStorage.getItem(DEVICE_KEY) } catch { /* use memory fallback */ }
  if (!id) {
    id = globalThis.crypto?.randomUUID?.() ?? `device-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
    try { localStorage.setItem(DEVICE_KEY, id) } catch { /* use memory fallback */ }
  }
  memoryDeviceId = id
  return id
}

export function createWorkspace(accountId, state = createDefaultDesktopState(), prefs = DESKTOP_DEFAULTS, productivity = PRODUCTIVITY_DEFAULTS) {
  const now = new Date().toISOString()
  return {
    version: 1,
    accountId,
    deviceId: desktopDeviceId(),
    revision: 0,
    baseRevision: 0,
    updatedAt: now,
    lastSyncedAt: null,
    pending: false,
    forceSync: false,
    state: normalizeDesktopState(state),
    prefs: normalizeDesktopPrefs(prefs),
    productivity: normalizeProductivity(productivity),
  }
}

export function normalizeWorkspace(value, accountId) {
  if (!value || typeof value !== "object") return null
  return {
    version: 1,
    accountId,
    deviceId: typeof value.deviceId === "string" ? value.deviceId.slice(0, 100) : desktopDeviceId(),
    revision: Math.max(0, Number(value.revision) || 0),
    baseRevision: Math.max(0, Number(value.baseRevision) || 0),
    updatedAt: typeof value.updatedAt === "string" ? value.updatedAt : new Date(0).toISOString(),
    lastSyncedAt: typeof value.lastSyncedAt === "string" ? value.lastSyncedAt : null,
    pending: !!value.pending,
    forceSync: !!value.forceSync,
    state: normalizeDesktopState(value.state),
    prefs: normalizeDesktopPrefs(value.prefs),
    productivity: normalizeProductivity(value.productivity),
  }
}

export function workspaceFromServer(serverPrefs, accountId) {
  const current = normalizeWorkspace(serverPrefs?.desktop_workspace, accountId)
  if (current) return current
  if (!serverPrefs?.desktop_layout && !serverPrefs?.desktop) return null
  const migrated = createWorkspace(
    accountId,
    serverPrefs.desktop_layout ?? createDefaultDesktopState(),
    serverPrefs.desktop ?? DESKTOP_DEFAULTS,
  )
  return { ...migrated, revision: 1, baseRevision: 1, lastSyncedAt: new Date().toISOString() }
}

export async function loadLocalWorkspace(accountId) {
  try {
    const db = await openDatabase()
    if (db) {
      const transaction = db.transaction(WORKSPACES, "readonly")
      const value = await requestResult(transaction.objectStore(WORKSPACES).get(accountId))
      db.close()
      const normalized = normalizeWorkspace(value, accountId)
      if (normalized) return normalized
    }
  } catch { /* localStorage fallback below */ }
  try {
    const fallback = normalizeWorkspace(JSON.parse(localStorage.getItem(fallbackKey(accountId)) ?? "null"), accountId)
    return fallback ?? migrateLegacyWorkspace(accountId)
  } catch {
    return migrateLegacyWorkspace(accountId)
  }
}

export async function saveLocalWorkspace(workspace) {
  const normalized = normalizeWorkspace(workspace, workspace.accountId)
  try {
    saveDesktopState(normalized.state, normalized.accountId)
    saveDesktopPrefs(normalized.prefs, normalized.accountId)
  } catch { /* IndexedDB remains the authoritative offline cache */ }
  try {
    const db = await openDatabase()
    if (db) {
      const transaction = db.transaction(WORKSPACES, "readwrite")
      transaction.objectStore(WORKSPACES).put(normalized)
      await transactionDone(transaction)
      db.close()
    }
  } catch { /* localStorage fallback below */ }
  try { localStorage.setItem(fallbackKey(normalized.accountId), JSON.stringify(normalized)) } catch { /* storage unavailable */ }
  return normalized
}

export async function saveWorkspaceSnapshot(workspace, reason = "automatic") {
  const snapshot = {
    ...normalizeWorkspace(workspace, workspace.accountId),
    id: `${workspace.accountId}:${Date.now()}:${Math.random().toString(36).slice(2, 7)}`,
    reason: String(reason).slice(0, 80),
    createdAt: new Date().toISOString(),
  }
  try {
    const db = await openDatabase()
    if (!db) {
      saveFallbackSnapshot(snapshot)
      return snapshot
    }
    const transaction = db.transaction(SNAPSHOTS, "readwrite")
    const store = transaction.objectStore(SNAPSHOTS)
    store.put(snapshot)
    const all = await requestResult(store.index("accountId").getAll(workspace.accountId))
    all.sort((left, right) => String(right.createdAt).localeCompare(String(left.createdAt)))
    all.slice(MAX_SNAPSHOTS).forEach((entry) => store.delete(entry.id))
    await transactionDone(transaction)
    db.close()
  } catch {
    saveFallbackSnapshot(snapshot)
  }
  return snapshot
}

export async function listWorkspaceSnapshots(accountId) {
  try {
    const db = await openDatabase()
    if (!db) return loadFallbackSnapshots(accountId)
    const transaction = db.transaction(SNAPSHOTS, "readonly")
    const all = await requestResult(transaction.objectStore(SNAPSHOTS).index("accountId").getAll(accountId))
    db.close()
    const merged = new Map([...all, ...loadFallbackSnapshots(accountId)].map((snapshot) => [snapshot.id, snapshot]))
    return [...merged.values()].sort((left, right) => String(right.createdAt).localeCompare(String(left.createdAt))).slice(0, MAX_SNAPSHOTS)
  } catch {
    return loadFallbackSnapshots(accountId)
  }
}

export function chooseWorkspace(local, remote, fallback) {
  if (!local && !remote) return { workspace: fallback, conflict: null }
  if (!local) return { workspace: remote, conflict: null }
  if (!remote) return { workspace: local, conflict: null }
  const concurrent = local.pending && remote.baseRevision > local.baseRevision && local.deviceId !== remote.deviceId
  if (concurrent) return { workspace: local, conflict: { local, remote } }
  if (local.pending) return { workspace: local, conflict: null }
  return {
    workspace: remote.baseRevision >= local.baseRevision ? remote : local,
    conflict: null,
  }
}

export function createWorkspaceChannel(accountId) {
  return typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(`holos-workspace:${accountId}`)
}
